import { motion, AnimatePresence } from 'framer-motion';
import { PadlockIcon, SkullIcon, TreasureChestIcon, KeyIcon, PuzzlePieceIcon, CompassIcon, TimerIcon, MagnifyingGlassIcon } from './EscapeIcons';
import { useLanguage } from '../context/LanguageContext';
import { useState } from 'react';
import { X } from 'lucide-react';
import { openLightbox } from './ImageLightbox';

const PROCESS_IMG = 'https://customer-assets.emergentagent.com/job_escape-nexus/artifacts/d7oo9zdt_image_2021_08_23T09_11_02_825Z.png';

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.5 } })
};

export default function HowItWorks() {
  const { t } = useLanguage();
  const [selectedCard, setSelectedCard] = useState(null);

  const cards = [
    {
      icon: PadlockIcon,
      title: t('everyonePlays'),
      description: t('everyoneDesc'),
      accent: t('everyoneAccent'),
      list: t('everyoneList'),
      // Bold neon-red gradient with skull + key floaters
      gradient: 'linear-gradient(135deg, #1a0000 0%, #4a0000 50%, #e32510 100%)',
      decorIcons: [
        { Icon: KeyIcon, size: 70, top: '12%', left: '12%', rotate: -25, opacity: 0.18 },
        { Icon: PuzzlePieceIcon, size: 56, top: '60%', left: '78%', rotate: 18, opacity: 0.18 },
        { Icon: CompassIcon, size: 48, top: '70%', left: '10%', rotate: 0, opacity: 0.15 },
      ],
    },
    {
      icon: SkullIcon,
      title: t('teamBuilding'),
      description: t('teamDesc'),
      accent: t('teamAccent'),
      list: null,
      // Dark moody with crimson accents
      gradient: 'linear-gradient(135deg, #000 0%, #1a0a0a 50%, #2a0000 100%)',
      decorIcons: [
        { Icon: PadlockIcon, size: 80, top: '18%', left: '70%', rotate: 12, opacity: 0.22 },
        { Icon: TimerIcon, size: 50, top: '65%', left: '15%', rotate: -8, opacity: 0.18 },
        { Icon: KeyIcon, size: 44, top: '70%', left: '78%', rotate: 45, opacity: 0.15 },
      ],
    },
    {
      icon: TreasureChestIcon,
      title: t('returnGames'),
      description: t('returnDesc'),
      accent: t('returnAccent'),
      list: null,
      // Crimson glow
      gradient: 'linear-gradient(135deg, #2a0000 0%, #e32510 100%)',
      decorIcons: [
        { Icon: MagnifyingGlassIcon, size: 64, top: '15%', left: '10%', rotate: -15, opacity: 0.18 },
        { Icon: PuzzlePieceIcon, size: 56, top: '60%', left: '72%', rotate: 22, opacity: 0.20 },
        { Icon: KeyIcon, size: 48, top: '12%', left: '76%', rotate: 35, opacity: 0.16 },
      ],
    },
  ];

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-4 sm:mb-6">
          <h2 className="font-heading text-2xl sm:text-5xl lg:text-6xl tracking-tight uppercase text-foreground">
            {t('playing')} <span className="text-brand">{t('okiEscape')}</span>
          </h2>
          <p className="font-sans text-sm sm:text-lg text-muted-foreground mt-2 sm:mt-4 max-w-xl mx-auto">{t('howItWorksDesc')}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} data-testid="process-infographic" className="mb-4 sm:mb-16 bg-card border border-border rounded-2xl sm:rounded-3xl overflow-hidden relative">
          <div className="absolute left-3 sm:left-6 lg:left-12 top-0 bottom-0 flex-col justify-around py-8 z-10 pointer-events-none hidden sm:flex">
            <motion.div animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 3, delay: 0 }}><KeyIcon size={48} className="text-white/30 dark:text-white/20 rotate-45" /></motion.div>
            <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 3.5, delay: 0.3 }}><PadlockIcon size={44} className="text-white/30 dark:text-white/20" /></motion.div>
            <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 2.8, delay: 0.6 }}><PuzzlePieceIcon size={48} className="text-white/30 dark:text-white/20 -rotate-12" /></motion.div>
            <motion.div animate={{ y: [0, 7, 0] }} transition={{ repeat: Infinity, duration: 3.2, delay: 0.9 }}><CompassIcon size={42} className="text-white/30 dark:text-white/20" /></motion.div>
          </div>
          <div className="absolute right-3 sm:right-6 lg:right-12 top-0 bottom-0 flex-col justify-around py-8 z-10 pointer-events-none hidden sm:flex">
            <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 3.3, delay: 0.2 }}><PadlockIcon size={44} className="text-white/30 dark:text-white/20 rotate-12" /></motion.div>
            <motion.div animate={{ y: [0, -7, 0] }} transition={{ repeat: Infinity, duration: 3, delay: 0.5 }}><KeyIcon size={50} className="text-white/30 dark:text-white/20 -rotate-45" /></motion.div>
            <motion.div animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 3.4, delay: 0.8 }}><TimerIcon size={44} className="text-white/30 dark:text-white/20" /></motion.div>
            <motion.div animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 2.9, delay: 1.1 }}><MagnifyingGlassIcon size={42} className="text-white/30 dark:text-white/20 rotate-12" /></motion.div>
          </div>
          <img src={PROCESS_IMG} alt="How to play OKI-ESCAPE" className="w-full h-auto max-h-[200px] sm:max-h-[400px] object-contain bg-brand/5 cursor-zoom-in" onClick={() => openLightbox(PROCESS_IMG, 'How to play OKI-ESCAPE')} />
        </motion.div>

        <div className="grid grid-cols-3 md:grid-cols-12 gap-3 sm:gap-6 lg:gap-8">
          {cards.map((card, i) => (
            <motion.div key={i} custom={i} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} data-testid={`how-card-${i}`} className="col-span-1 md:col-span-4 bg-card border border-border rounded-2xl sm:rounded-3xl overflow-hidden hover:border-brand/50 transition-colors duration-300 group relative cursor-pointer sm:cursor-default" onClick={() => { if (window.innerWidth < 640) setSelectedCard(card); }}>
              {/* Icon-driven brand header (replaces stock photo) */}
              <div className="relative h-20 sm:h-48 overflow-hidden flex items-center justify-center" style={{ background: card.gradient }}>
                {/* Floating decorative icons */}
                {card.decorIcons.map(({ Icon, size, top, left, rotate, opacity }, k) => (
                  <motion.div
                    key={k}
                    className="absolute pointer-events-none hidden sm:block"
                    style={{ top, left, opacity }}
                    animate={{ y: [0, k % 2 === 0 ? -8 : 8, 0], rotate: [rotate, rotate + 4, rotate] }}
                    transition={{ repeat: Infinity, duration: 3 + k * 0.4, ease: 'easeInOut' }}
                  >
                    <Icon size={size} className="text-white" />
                  </motion.div>
                ))}
                {/* Center hero icon */}
                <motion.div
                  className="relative z-10"
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{ repeat: Infinity, duration: 2.8, ease: 'easeInOut' }}
                >
                  <div
                    className="rounded-full p-3 sm:p-6 backdrop-blur-sm bg-black/30 border border-white/15 shadow-[0_0_40px_rgba(227,37,16,0.5)] group-hover:shadow-[0_0_60px_rgba(227,37,16,0.7)] transition-shadow duration-500"
                  >
                    <card.icon size={28} className="text-white sm:hidden" />
                    <card.icon size={64} className="text-white hidden sm:block" />
                  </div>
                </motion.div>
                {/* Subtle vignette */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
              </div>
              <div className="p-2.5 sm:p-8 sm:p-10">
                <div className="flex items-center gap-1 sm:gap-3 mb-1.5 sm:mb-4">
                  <card.icon className="text-brand flex-shrink-0" size={14} />
                  <h3 className="font-heading text-[11px] sm:text-2xl sm:text-3xl tracking-tight uppercase text-foreground leading-tight">{card.title}</h3>
                </div>
                <p className="font-sans text-[9px] sm:text-base text-muted-foreground leading-snug">{card.description}</p>
                <p className="font-sans text-[9px] sm:text-sm text-brand font-semibold mt-1.5 sm:mt-4 leading-snug">{card.accent}</p>
                {card.list && <p className="font-sans text-[8px] sm:text-xs text-muted-foreground mt-1 sm:mt-3 leading-snug italic">{card.list}</p>}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile detail popup */}
        <AnimatePresence>
          {selectedCard && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedCard(null)}
                className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm sm:hidden"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="fixed inset-4 z-[10000] sm:hidden flex items-center justify-center"
              >
                <div className="bg-card border border-border rounded-3xl overflow-hidden w-full max-h-[80vh] overflow-y-auto" data-testid="how-detail-modal">
                  <div className="relative h-48 overflow-hidden flex items-center justify-center" style={{ background: selectedCard.gradient }}>
                    {selectedCard.decorIcons.map(({ Icon, size, top, left, rotate, opacity }, k) => (
                      <div key={k} className="absolute pointer-events-none" style={{ top, left, opacity, transform: `rotate(${rotate}deg)` }}>
                        <Icon size={size} className="text-white" />
                      </div>
                    ))}
                    <div className="relative z-10 rounded-full p-6 backdrop-blur-sm bg-black/30 border border-white/15 shadow-[0_0_40px_rgba(227,37,16,0.5)]">
                      <selectedCard.icon size={64} className="text-white" />
                    </div>
                    <button
                      onClick={() => setSelectedCard(null)}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white z-20"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <selectedCard.icon className="text-brand" size={22} />
                      <h3 className="font-heading text-2xl tracking-tight uppercase text-foreground">{selectedCard.title}</h3>
                    </div>
                    <p className="font-sans text-sm text-muted-foreground leading-relaxed">{selectedCard.description}</p>
                    <p className="font-sans text-sm text-brand font-semibold mt-4">{selectedCard.accent}</p>
                    {selectedCard.list && <p className="font-sans text-sm text-muted-foreground mt-3 italic">{selectedCard.list}</p>}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
